import type { VoiceProvider } from '@/api/voiceSession'
import type { PostHog } from 'posthog-js'
import { isSensitiveLocation, redactSensitiveURLPath, sanitizeCapturedEvent } from './publicRoute'

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string) || 'https://eu.i.posthog.com'

const PUBLIC_PAUSE_MARKER = 'peatch_ph_public_pause'

let instance: PostHog | undefined
let initRequested = false
let publicRoutePaused = false
let initDeferred = false
let recordingActiveBeforePause = false

const MAX_QUEUED_CALLS = 200
let pendingCalls: Array<(ph: PostHog) => void> = []

function hasValidKey(): boolean {
  return !!POSTHOG_KEY && typeof POSTHOG_KEY === 'string' && POSTHOG_KEY.startsWith('phc_')
}

function readPauseMarker(): boolean {
  try {
    return window.localStorage.getItem(PUBLIC_PAUSE_MARKER) === '1'
  } catch {
    return false
  }
}

function writePauseMarker(): void {
  try {
    window.localStorage.setItem(PUBLIC_PAUSE_MARKER, '1')
  } catch {
    return
  }
}

function clearPauseMarker(): void {
  try {
    window.localStorage.removeItem(PUBLIC_PAUSE_MARKER)
  } catch {
    return
  }
}

function run(fn: (ph: PostHog) => void): void {
  if (
    typeof window !== 'undefined' &&
    isSensitiveLocation(window.location.pathname, window.location.search)
  ) {
    return
  }
  if (instance) {
    fn(instance)
    return
  }
  if (initRequested && hasValidKey() && pendingCalls.length < MAX_QUEUED_CALLS) {
    pendingCalls.push(fn)
  }
}

export function initPostHog(): void {
  if (initRequested) {
    return
  }

  if (typeof window === 'undefined') {
    return
  }

  if (import.meta.env.DEV && import.meta.env.VITE_POSTHOG_DEV !== 'true') {
    console.log('PostHog: disabled in dev (set VITE_POSTHOG_DEV=true to enable)')
    return
  }

  if (!hasValidKey()) {
    if (import.meta.env.DEV) {
      console.log(
        'PostHog: API key not configured (set VITE_POSTHOG_KEY), analytics disabled in dev mode'
      )
    }
    return
  }

  initRequested = true

  const schedule =
    'requestIdleCallback' in window
      ? window.requestIdleCallback
      : (cb: () => void) => window.setTimeout(cb, 1500)

  schedule(() => {
    void loadAndInit()
  })
}

async function loadAndInit(): Promise<void> {
  if (instance || !POSTHOG_KEY) {
    return
  }

  if (publicRoutePaused) {
    initDeferred = true
    return
  }

  if (import.meta.env.DEV) {
    console.log('PostHog: Initializing with host:', POSTHOG_HOST)
  }

  let posthog: PostHog
  try {
    posthog = (await import('posthog-js')).default
  } catch (err) {
    console.warn('PostHog: failed to load SDK', err)
    pendingCalls = []
    return
  }

  if (publicRoutePaused || isSensitiveLocation(window.location.pathname, window.location.search)) {
    initDeferred = true
    return
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    defaults: '2025-11-30',
    person_profiles: 'identified_only',
    disable_compression: false,
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    // SECURITY: Use memory storage to avoid PII persistence in localStorage
    persistence: 'memory',
    // SECURITY: Block sensitive properties from being sent to analytics
    // Note: Do NOT include 'api_key' here - it breaks PostHog's own auth
    property_denylist: [
      'name',
      'phone',
      'resume',
      'password',
      'credit_card',
      'ssn',
      'address',
      'candidate_name',
      'candidate_email',
      'hiring_manager_email',
      'user_token',
      'auth_token',
    ],
    before_send: sanitizeCapturedEvent,
    disable_session_recording: import.meta.env.DEV,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-ph-mask]',
    },
    debug: import.meta.env.DEV,
    on_request_error: (error) => {
      console.warn('PostHog request error:', error)
    },
    loaded: (ph) => {
      if (import.meta.env.DEV) {
        console.log('PostHog: Loaded successfully')
        console.log('PostHog: Distinct ID:', ph.get_distinct_id())
        console.log('PostHog: API host:', POSTHOG_HOST)
        console.log('PostHog: Debug mode enabled - check Network tab for /e/ requests')
      }
    },
  })

  instance = posthog
  initDeferred = false

  if (readPauseMarker()) {
    posthog.opt_in_capturing()
    clearPauseMarker()
  }

  const queued = pendingCalls
  pendingCalls = []
  for (const fn of queued) {
    run(fn)
  }
}

export function isPostHogEnabled(): boolean {
  return initRequested && hasValidKey()
}

export function identifyUser(
  userId: string | number,
  userType: 'candidate' | 'hiring_manager' | 'admin',
  traits?: Record<string, unknown>
): void {
  run((ph) =>
    ph.identify(String(userId), {
      user_type: userType,
      ...traits,
    })
  )
}

export function resetUser(): void {
  run((ph) => ph.reset())
}

export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  run((ph) => ph.capture(event, properties))
}

export function trackInterviewEvent(
  event: string,
  sessionId: number,
  properties?: Record<string, unknown>
): void {
  trackEvent(event, {
    kind: 'hiring',
    session_id: sessionId,
    ...properties,
  })
}

export function trackInterviewStarted(sessionId: number, scenarioId?: number): void {
  trackInterviewEvent('interview_started', sessionId, {
    scenario_id: scenarioId,
  })
}

export function trackInterviewConnected(
  sessionId: number,
  scenarioId?: number,
  voiceProvider?: VoiceProvider,
  connectMs?: number
): void {
  trackInterviewEvent('interview_connected', sessionId, {
    scenario_id: scenarioId,
    voice_provider: voiceProvider,
    connect_ms: connectMs,
  })
}

const CONNECT_ERROR_MESSAGE_LIMIT = 300

export function trackInterviewConnectFailed(
  sessionId: number,
  error: unknown,
  connectMs: number,
  voiceProvider?: VoiceProvider
): void {
  const message = error instanceof Error ? error.message : String(error)
  trackInterviewEvent('interview_connect_failed', sessionId, {
    voice_provider: voiceProvider,
    connect_ms: connectMs,
    error_name: error instanceof Error ? error.name : typeof error,
    error_message: message.slice(0, CONNECT_ERROR_MESSAGE_LIMIT),
  })
}

export type InterviewEndedBy =
  | 'candidate'
  | 'agent'
  | 'time_limit'
  | 'candidate_dropped'
  | 'points_confirmed'

export function trackInterviewCompleted(
  sessionId: number,
  durationSeconds: number,
  voiceProvider?: VoiceProvider,
  endedBy?: InterviewEndedBy,
  unusableCandidateTurns?: number,
  score?: number
): void {
  trackInterviewEvent('interview_completed', sessionId, {
    duration_seconds: durationSeconds,
    voice_provider: voiceProvider,
    ended_by: endedBy,
    unusable_candidate_turns: unusableCandidateTurns,
    score,
  })
}

export interface VoiceSessionClose {
  reason?: string | undefined
  closeCode?: number | undefined
  closeReason?: string | undefined
  message?: string | undefined
}

export function trackVoiceSessionClosed(
  sessionId: number,
  voiceProvider: VoiceProvider,
  close: VoiceSessionClose
): void {
  trackInterviewEvent('voice_session_closed', sessionId, {
    voice_provider: voiceProvider,
    reason: close.reason,
    close_code: close.closeCode,
    close_reason: close.closeReason,
    message: close.message,
  })
}

export function trackPitchGenerated(targetCompany: string, targetRole: string): void {
  trackEvent('pitch_generated', {
    target_company: targetCompany,
    target_role: targetRole,
  })
}

export function trackInviteSent(batchId: number, count: number): void {
  trackEvent('invite_sent', {
    batch_id: batchId,
    count,
  })
}

export function trackFeatureUsed(featureName: string, properties?: Record<string, unknown>): void {
  trackEvent('feature_used', {
    feature_name: featureName,
    ...properties,
  })
}

export function trackPageView(path: string, properties?: Record<string, unknown>): void {
  run((ph) =>
    ph.capture('$pageview', {
      $current_url: redactSensitiveURLPath(path),
      ...properties,
    })
  )
}

export function setUserProperties(properties: Record<string, unknown>): void {
  run((ph) => ph.people.set(properties))
}

export function getDistinctId(): string | undefined {
  return instance?.get_distinct_id()
}

export function pauseAnalyticsForPublicRoute(): () => void {
  publicRoutePaused = true

  if (instance) {
    recordingActiveBeforePause = instance.sessionRecordingStarted()
    instance.opt_out_capturing()
    instance.stopSessionRecording()
    writePauseMarker()
  }

  return () => {
    publicRoutePaused = false

    if (instance) {
      instance.opt_in_capturing()
      if (recordingActiveBeforePause) {
        instance.startSessionRecording()
      }
      clearPauseMarker()
      return
    }

    if (
      initDeferred &&
      typeof window !== 'undefined' &&
      !isSensitiveLocation(window.location.pathname, window.location.search)
    ) {
      initDeferred = false
      void loadAndInit()
    }
  }
}
